   
   import React, { useState, useEffect, useContext } from 'react';
   import { Container, Button, Typography, CircularProgress, Avatar, Paper } from '@mui/material';
   import { CameraAlt, Upload } from '@mui/icons-material';
   import CustomCamera from './CustomCamera';
   import { UserContext } from '../context/UserContext';
   import { registerUser, fetchDocuments, fetchProtectedData, uploadFile, GetSavedUserPhoto } from '../services/api';
   import DocumentList from './DocumentList'; 
   import PaymentButton from './PaymentButton';

   
   function Profile() {
     const { userGuid, firstName, lastName, email, accessToken } = useContext(UserContext);
     const [showCamera, setShowCamera] = useState(false);
     const [uploadStatus, setUploadStatus] = useState('');
     const [isUploading, setIsUploading] = useState(false);
     const [capturedPhoto, setCapturedPhoto] = useState(null);
     const [isProfileSetup, setIsProfileSetup] = useState(false);
     const [documents, setDocuments] = useState([]);
     const [documentFetchStatus, setDocumentFetchStatus] = useState('');
     const [isPaid, setIsPaid] = useState(false);
     const [isLoading, setIsLoading] = useState(true);
     const [userData, setUserData] = useState({ firstName: '', lastName: '' });
     const [savedPhoto, setSavedPhoto] = useState(null);
   
     /*
     console.log('Profile component rendering with:', { 
      userGuid, 
      accessToken: accessToken ? 'Present' : 'Missing',
      isProfileSetup,
      isPaid
    });
    */

     useEffect(() => {
       const checkProfileSetup = async () => {
        console.log('checkProfileSetup running with:', { userGuid, accessToken});

         if (userGuid && accessToken) {
           setIsLoading(true);
           try {
             const response = await fetchProtectedData('/get-user', userGuid, accessToken);
             //console.log('fetchProtectedData response:', response);
             //console.log('firstname from response: ', response.firstName)
             //console.log('Paid_Status from response: ', response.Paid_Status)
            
             // Update user data state with the response data
             setUserData({
              firstName: response.firstName || firstName,
              lastName: response.lastName || lastName
            });
             // Set paid status from lambda response from dynamo to constant
             setIsPaid(response.Paid_Status === 'True' || response.Paid_Status === true);

               if (response.rekognitionid !== 'Pending') {
               setIsProfileSetup(false);
               //console.log('isProfileSetup set to: ', response.rekognitionid);

               try {
                const photoResponse = await GetSavedUserPhoto(userGuid, accessToken);
                //console.log('GetSavedUserPhoto response:', photoResponse);
                
                if (photoResponse.success && photoResponse.photo) {
                  setSavedPhoto(photoResponse.photo); // Set the base64 photo
                } else {
                  console.log('Saved User photo not found.');
                }
              } catch (photoError) {
                console.error('Error fetching saved user photo:', photoError);
              }

             } else if (response.rekognitionid === 'Pending') {
               setIsProfileSetup(true);
               console.log('isProfileSetup is set to:', response.rekognitionid);
             } else {
               console.error('Failed to fetch user data:', response.message);
             }
           } catch (error) {
             console.error('Error checking profile setup:', error);
           } finally {
             setIsLoading(false);
           }
         }
       };
   
       checkProfileSetup();
     }, [userGuid, accessToken, firstName, lastName]);
   
     const handleCapture = async (dataUrl) => {
       setCapturedPhoto(dataUrl);
       setShowCamera(false);
       setUploadStatus('Uploading training photo...');
       setIsUploading(true);
   
       try {
         const response = await registerUser({ image: dataUrl, userGuid, firstName, lastName, email });
         if (response.success) {
           setUploadStatus('Profile photo uploaded and verified successfully!');
           setIsProfileSetup(false);
         } else {
           setUploadStatus('Failed to upload profile photo.');
         }
       } catch (error) {
         console.error('Error uploading training photo:', error);
         setUploadStatus('Error uploading training photo.');
       } finally {
         setIsUploading(false);
       }
     };

     const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
  
      setUploadStatus('Uploading document...');
      setIsUploading(true);
  
      try {
        const response = await uploadFile(userGuid, file, 'POLST', accessToken);
        
        if (response.success) {
          setUploadStatus('Document uploaded successfully!');
          fetchUserDocuments(); // Refresh the documents list
        } else {
          setUploadStatus('Failed to upload document.');
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        setUploadStatus(error.message || 'Error uploading document.');
      } finally {
        setIsUploading(false);
      }
    };

    const refreshDocuments = () => {
      fetchUserDocuments();
    };

     const fetchUserDocuments = async () => {
      setDocumentFetchStatus('Fetching documents...');
      try {
        const response = await fetchDocuments(userGuid, accessToken);
        console.log('Documents response:', response);
        
        if (response.files) {
          setDocuments(response.files);  // This will update the documents state
          setDocumentFetchStatus('');
        } else {
          setDocumentFetchStatus('No documents found.');
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setDocumentFetchStatus('Error fetching documents.');
      }
    };
   
     useEffect(() => {
       if (userGuid && accessToken && !isProfileSetup) {
         fetchUserDocuments();
       }
     }, [userGuid, accessToken, isProfileSetup]);
   
     return (
       <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
         {isLoading ? (
           <CircularProgress sx={{ mt: 2 }} />
         ) : (
           <>
             {isProfileSetup ? (
               <Paper sx={{ padding: 3 }}>
                 <Typography variant="body1" sx={{ mb: 2 }}>
                   To set up Check4DNR, you need to register your face. Please take a clear, well-lit photo with only yourself in the frame.
                 </Typography>
                 <Button
                   variant="contained"
                   startIcon={<CameraAlt />}
                   onClick={() => setShowCamera(true)}
                   disabled={isUploading}
                 >
                   Register Face
                 </Button>
               </Paper>
             ) : (
               <>
                 <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
                   Welcome {userData.firstName} {userData.lastName}
                 </Typography>
                 {savedPhoto && (
                   <Avatar
                     src={`data:image/jpeg;base64,${savedPhoto}`}
                     alt={`${userData.firstName} ${userData.lastName}`}
                     sx={{ 
                       width: 120, 
                       height: 120, 
                       margin: '0 auto',
                       mb: 3,
                       border: '3px solid #f0f0f0',
                       boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                     }}
                   />
                 )}
               </>
             )}
           </>
         )}
   
         {isUploading && <CircularProgress sx={{ mt: 2 }} />}
   
         {uploadStatus && (
           <Typography
             variant="body1"
             sx={{
               mt: 3,
               color: uploadStatus.toLowerCase().includes('success') ? 'green' : 'red',
             }}
           >
             {uploadStatus}
           </Typography>
         )}
   
         {capturedPhoto && (
           <div style={{ marginTop: 20 }}>
             <Typography variant="h6">Captured Training Photo:</Typography>
             <Avatar
               src={capturedPhoto}
               alt="Captured"
               sx={{ width: 120, height: 120, margin: '0 auto' }}
             />
           </div>
         )}
   
         {showCamera && (
           <CustomCamera onCapture={handleCapture} onClose={() => setShowCamera(false)} />
         )}
   
         {!isProfileSetup && (
           <div style={{ marginTop: 40 }}>
             <Typography variant="h5" gutterBottom>
               Your Documents
             </Typography>
             {documentFetchStatus && (
               <Typography variant="body1" sx={{ mt: 2, color: 'red' }}>
                 {documentFetchStatus}
               </Typography>
             )}
             <DocumentList 
               userGuid={userGuid} 
               documents={documents}
               accessToken={accessToken}
               onDocumentDeleted={refreshDocuments}
             />
             
             {isPaid ? (
               <Button
                 variant="contained"
                 component="label"
                 startIcon={<Upload />}
                 sx={{ mt: 2, mb: 2 }}
                 disabled={isUploading}
               >
                 Upload Document
                 <input
                   type="file"
                   hidden
                   accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,"
                   onChange={handleFileUpload}
                 />
               </Button>
             ) : (
               <PaymentButton />
             )}  
           </div>
         )}
       </Container>
     );
   }
   
   export default Profile;
   